import kebabCase from "lodash/kebabCase";
import ValidationErrors from "@/components/inertia/validation-errors";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { stores_path } from "@/lib/routes";
import { InertiaFormProps } from "@inertiajs/react";
import { ChangeEvent, FormEvent } from "react";
import useTranslations from "@/hooks/use-translations";

export type NewStoreFormData = {
  store: {
    name: string;
    subdomain: string;
    currency: string;
  };
};

export default function NewStoreForm({
  form,
  onSubmitForm,
}: {
  form: InertiaFormProps<NewStoreFormData>;
  onSubmitForm?: () => void;
}) {
  const { t } = useTranslations("shared.components.new_store_form");

  function onChangeName(event: ChangeEvent<HTMLInputElement>) {
    form.setData("store.name", event.target.value);
    form.setData("store.subdomain", kebabCase(event.target.value));
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    form.post(stores_path(), {
      onFinish() {
        onSubmitForm?.();
      },
    });
  }

  return (
    <form
      id="new-store-form"
      onSubmit={onSubmit}
      className="flex flex-col gap-5"
    >
      <Field data-invalid={!!form.errors["store.name"]}>
        <FieldLabel htmlFor="name">{t("name_label")}</FieldLabel>

        <div className="flex flex-col gap-0">
          <Input
            id="name"
            name="store[name]"
            type="text"
            placeholder={t("name_placeholder")}
            className="bg-white border-b-0 rounded-b-none focus:z-1"
            aria-invalid={!!form.errors["store.name"]}
            value={form.data.store.name}
            onChange={onChangeName}
          />
          <div className="border-t">
            <InputGroup className="group-focus:z-1 border-t-0 rounded-t-none">
              <InputGroupInput
                id="subdomain"
                name="store[subdomain]"
                type="text"
                placeholder="subdomain"
                value={form.data.store.subdomain}
                onChange={(event) =>
                  form.setData("store.subdomain", event.target.value)
                }
                aria-invalid={!!form.errors["store.name"]}
              />
              <InputGroupAddon>
                <InputGroupText>https://</InputGroupText>
              </InputGroupAddon>
              <InputGroupAddon align="inline-end">
                <InputGroupText>.brickgestor.app</InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </div>
        </div>

        {form.errors["store.name"] && (
          <ValidationErrors errors={form.errors["store.name"]} />
        )}
      </Field>

      <Field data-invalid={!!form.errors["store.currency"]}>
        <FieldLabel>{t("currency_label")}</FieldLabel>

        <Select
          name="store[currency]"
          defaultValue="usd"
          value={form.data.store.currency}
          onValueChange={(value) => form.setData("store.currency", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("currency_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="usd">USD</SelectItem>
            <SelectItem value="eur">EUR</SelectItem>
            <SelectItem value="pen">PEN</SelectItem>
          </SelectContent>
        </Select>

        {form.errors["store.currency"] && (
          <ValidationErrors errors={form.errors["store.currency"]} />
        )}
      </Field>
    </form>
  );
}
